import { CurrencyCode } from '../constants'
import { BuyInType, FormatType, LimitType, TournamentType } from '../FilterManager'
import { Injector } from '../injector'
import { GameType, LobbyManager } from '../LobbyManager'
import { Settings } from '../Settings'
import { FilterOption } from '../ui/lobby/menu/filter/FilterComponent'
import { Filter } from './LobbyFilters'
import { Game, RingGameFeature } from '../data/LobbyData'
import { FeatureToggleService } from '../services/FeatureToggleService'
import { AuthService } from '../auth/AuthService'

export const sweepsMicroUpperLimit = 0.1
export const sweepsLowUpperLimit = 1
export const sweepsMediumUpperLimit = 8

export const goldMicroUpperLimit = 50
export const goldLowUpperLimit = 2000
export const goldMediumUpperLimit = 50000

export interface GameObject {
    id: number;
		name: string;
		features: RingGameFeature[] | undefined,
    variant: FormatType;
    type: LimitType;
    buyIn: string;
    bigBlind: string;
    buyInCurrencyCode: CurrencyCode;
    currencyCode: CurrencyCode;
    isSatelliteTournament: boolean;
    category: string;
    isRebuyEnabled: boolean;
    satelliteTicket?: string;
    hasBounty: boolean;
    bountyFee: number;
    mysteryBountyFee: number;
    mysteryBountyGuaranteedPrizePool: number;
}

export class FormatFilter extends Filter {

	showHoldEm = Settings.isEnabled(Settings.Param.FILTER_FORMAT_HOLDEM_ENABLED, this.isPokerFormatDefaultShown(FormatType.HOLDEM))
	showOmaha = Settings.isEnabled(Settings.Param.FILTER_FORMAT_OMAHA_ENABLED, this.isPokerFormatDefaultShown(FormatType.OMAHA))
	showOmahaHL = Settings.isEnabled(Settings.Param.FILTER_FORMAT_OMAHA_HL_ENABLED, this.isPokerFormatDefaultShown(FormatType.OMAHA_HL))
	showPineapple = Settings.isEnabled(Settings.Param.FILTER_FORMAT_PINEAPPLE_ENABLED, this.isPokerFormatDefaultShown(FormatType.PINEAPPLE))

	updateFilter(filterOptions: Array<FilterOption<FormatType>>): void {
		filterOptions.forEach(option => {
			switch (option.key) {
			case FormatType.HOLDEM:
				this.showHoldEm = option.state
				Settings.setProperty(Settings.Param.FILTER_FORMAT_HOLDEM_ENABLED, this.showHoldEm)
				break
			case FormatType.OMAHA:
				this.showOmaha = option.state
				Settings.setProperty(Settings.Param.FILTER_FORMAT_OMAHA_ENABLED, this.showOmaha)
				break
			case FormatType.PINEAPPLE:
				this.showPineapple = option.state
				Settings.setProperty(Settings.Param.FILTER_FORMAT_PINEAPPLE_ENABLED, this.showPineapple)
				break
			case FormatType.OMAHA_HL:
				this.showOmahaHL = option.state
				Settings.setProperty(Settings.Param.FILTER_FORMAT_OMAHA_HL_ENABLED, this.showOmahaHL)
				break
			default:
				assertNever(option.key)
			}
		})
	}

	filter(gameItem: GameObject): boolean {
		switch (gameItem.variant) {
		case FormatType.HOLDEM:
			return this.showHoldEm
		case FormatType.OMAHA:
			return this.showOmaha
		case FormatType.PINEAPPLE:
			return this.showPineapple
		case FormatType.OMAHA_HL:
			return this.showOmahaHL
		default:
			return false
		}
	}

	private isPokerFormatDefaultShown(variant: FormatType): boolean {
		if (!FeatureToggleService.findGameFeature.isEnabled()) return true
		return this.registeredLessThan30DaysAgo() ? variant === FormatType.HOLDEM : true
	}

	private registeredLessThan30DaysAgo(): boolean {
		const registrationTimeMillis = AuthService.getUserRegistrationDate().getTime()

		const thirtyDaysMillis = 60 * 60 * 24 * 30 * 1000
		const fiveMinMillis = 60 * 5 * 1000
		const newUserTimeIntervalMillis = FeatureToggleService.findGameFeature.newUserTimeIntervalIs5MinTestingOnly() ? fiveMinMillis : thirtyDaysMillis

		return (Date.now() - registrationTimeMillis) < newUserTimeIntervalMillis
	}
}

export class LimitsFilter extends Filter {

	showNoLimit = Settings.isEnabled(Settings.Param.FILTER_LIMITS_NONE_ENABLED, true)
	showPotLimit = Settings.isEnabled(Settings.Param.FILTER_LIMITS_POT_ENABLED, true)
	showFixedLimit = Settings.isEnabled(Settings.Param.FILTER_LIMITS_FIXED_ENABLED, true)

	updateFilter(filterOptions: Array<FilterOption<LimitType>>): void {
		filterOptions.forEach(option => {
			switch (option.key) {
			case LimitType.NONE:
				this.showNoLimit = option.state
				Settings.setProperty(Settings.Param.FILTER_LIMITS_NONE_ENABLED, this.showNoLimit)
				break
			case LimitType.POT:
				this.showPotLimit = option.state
				Settings.setProperty(Settings.Param.FILTER_LIMITS_POT_ENABLED, this.showPotLimit)
				break
			case LimitType.FIXED:
				this.showFixedLimit = option.state
				Settings.setProperty(Settings.Param.FILTER_LIMITS_FIXED_ENABLED, this.showFixedLimit)
				break
			default:
				assertNever(option.key)
			}
		})
	}

	filter(gameItem: GameObject): boolean {
		switch (gameItem.type) {
		case LimitType.NONE:
			return this.showNoLimit
		case LimitType.POT:
			return this.showPotLimit
		case LimitType.FIXED:
			return this.showFixedLimit
		default:
			assertNever(gameItem.type)
		}
	}
}

export class BuyInFilter extends Filter {
	private showMicro: boolean
	private showLow: boolean
	private showMedium: boolean
	private showHigh: boolean

	private lobbyManager: LobbyManager

	getFilterStates() {
		this.loadSavedStates()
		return {
			showMicro: this.showMicro,
			showLow: this.showLow,
			showMedium: this.showMedium,
			showHigh: this.showHigh,
		}
	}

	updateFilter(filterOptions: Array<FilterOption<BuyInType>>): void {
		this.loadSavedStates()
		filterOptions.forEach(option => {
			switch (option.key) {
			case BuyInType.MICRO:
				this.showMicro = option.state
				Settings.setProperty(Settings.Param.FILTER_BUY_IN_MICRO_ENABLED, this.showMicro)
				break
			case BuyInType.LOW:
				this.showLow = option.state
				Settings.setProperty(Settings.Param.FILTER_BUY_IN_LOW_ENABLED, this.showLow)
				break
			case BuyInType.MEDIUM:
				this.showMedium = option.state
				Settings.setProperty(Settings.Param.FILTER_BUY_IN_MEDIUM_ENABLED, this.showMedium)
				break
			case BuyInType.HIGH:
				this.showHigh = option.state
				Settings.setProperty(Settings.Param.FILTER_BUY_IN_HIGH_ENABLED, this.showHigh)
				break
			default:
				assertNever(option.key)
			}
		})
	}

	filter(gameItem: GameObject): boolean {
		// when called from the LobbyData class for the new Poker Server Lobby Service
		// the object being passed in is a Game type (either SitAndGo or RingGame)
		// not a GameObject.
		// Will perform a check on the gameType which only exists on the Game type to determine object type
		const isLobbyData = typeof(gameItem['gameType']) !== 'undefined'
		if (isLobbyData) {
			// the this.lobbyManager is not available
			// when first opening the browser when using the new Poker Service Lobby Service
			const item = gameItem as unknown as Game
			return item.gameType === GameType.Ring
				? this.shouldAllow_ringGameLogic(gameItem)
				: this.shouldAllow_tournamentLogic(gameItem)
		} else {
			this.lobbyManager = this.lobbyManager || Injector.get(LobbyManager)
			const gameType = this.lobbyManager.state
			return gameType === GameType.Ring
				? this.shouldAllow_ringGameLogic(gameItem)
				: this.shouldAllow_tournamentLogic(gameItem)
		}
	}

	shouldAllow_ringGameLogic(gameItem: GameObject): boolean {
		this.loadSavedStates()

		let microUpperLimit = sweepsMicroUpperLimit
		let lowUpperLimit = sweepsLowUpperLimit
		let mediumUpperLimit = sweepsMediumUpperLimit
		if (gameItem.currencyCode === CurrencyCode.GOLD_COINS) {
			microUpperLimit = goldMicroUpperLimit
			lowUpperLimit = goldLowUpperLimit
			mediumUpperLimit = goldMediumUpperLimit
		}

		const bigBlind = Number(gameItem.bigBlind)

		if (this.showMicro && bigBlind <= microUpperLimit) {
			return true
		}
		if (this.showLow && (bigBlind > microUpperLimit && bigBlind <= lowUpperLimit)) {
			return true
		}
		if (this.showMedium && (bigBlind > lowUpperLimit && bigBlind <= mediumUpperLimit)) {
			return true
		}
		return this.showHigh && bigBlind > mediumUpperLimit
	}

	shouldAllow_tournamentLogic(gameItem: GameObject): boolean {
		this.loadSavedStates()
		let microUpperLimit = 2
		let lowUpperLimit = 10
		let mediumUpperLimit = 50
		const buyIn = Number(gameItem.buyIn) + Number(gameItem?.bountyFee ?? '0')

		if (gameItem.buyInCurrencyCode === CurrencyCode.GOLD_COINS) {
			microUpperLimit = 2000
			lowUpperLimit = 10000
			mediumUpperLimit = 40000
		}

		if (this.showMicro && buyIn <= microUpperLimit) {
			return true
		}
		if (this.showLow && (buyIn > microUpperLimit && buyIn <= lowUpperLimit)) {
			return true
		}
		if (this.showMedium && (buyIn > lowUpperLimit && buyIn <= mediumUpperLimit)) {
			return true
		}
		return this.showHigh && buyIn > mediumUpperLimit
	}

	private loadSavedStates() {
		// Only load once, if already set ignore.
		if (this.showMicro != undefined) return

		this.showMicro = Settings.isEnabled(Settings.Param.FILTER_BUY_IN_MICRO_ENABLED, true)
		this.showLow = Settings.isEnabled(Settings.Param.FILTER_BUY_IN_LOW_ENABLED, true)
		this.showMedium = Settings.isEnabled(Settings.Param.FILTER_BUY_IN_MEDIUM_ENABLED, true)
		this.showHigh = Settings.isEnabled(Settings.Param.FILTER_BUY_IN_HIGH_ENABLED, true)
	}
}

export class TournamentTypeFilter extends Filter {

	showRegular = Settings.isEnabled(Settings.Param.FILTER_TT_REGULAR_ENABLED, true)
	showGuaranteed = Settings.isEnabled(Settings.Param.FILTER_TT_GUARANTEED_ENABLED, true)
	showSatellite = Settings.isEnabled(Settings.Param.FILTER_TT_SATELLITE_ENABLED, true)
	showRebuy = Settings.isEnabled(Settings.Param.FILTER_TT_REBUY_ENABLED, true)
	showBounty = Settings.isEnabled(Settings.Param.FILTER_TT_BOUNTY_ENABLED, true)

	updateFilter(filterOptions: Array<FilterOption<TournamentType>>): void {
		filterOptions.forEach(option => {
			switch (option.key) {
			case TournamentType.REGULAR:
				this.showRegular = option.state
				Settings.setProperty(Settings.Param.FILTER_TT_REGULAR_ENABLED, this.showRegular)
				break
			case TournamentType.GUARANTEED:
				this.showGuaranteed = option.state
				Settings.setProperty(Settings.Param.FILTER_TT_GUARANTEED_ENABLED, this.showGuaranteed)
				break
			case TournamentType.SATELLITE:
				this.showSatellite = option.state
				Settings.setProperty(Settings.Param.FILTER_TT_SATELLITE_ENABLED, this.showSatellite)
				break
			case TournamentType.REBUY:
				this.showRebuy = option.state
				Settings.setProperty(Settings.Param.FILTER_TT_REBUY_ENABLED, this.showRebuy)
				break
			case TournamentType.BOUNTY:
				this.showBounty = option.state
				Settings.setProperty(Settings.Param.FILTER_TT_BOUNTY_ENABLED, this.showBounty)
				break
			default:
				assertNever(option.key)
			}
		})
	}

	filter(gameItem: GameObject): boolean {
		return ((this.isRegular(gameItem) || this.isStageTournament(gameItem)) && this.showRegular) ||
            (this.isGuaranteed(gameItem) && this.showGuaranteed) ||
            (this.isRebuy(gameItem) && this.showRebuy) ||
            ((this.hasBounty(gameItem) || this.hasMysteryBounty(gameItem)) && this.showBounty) ||
            (this.isSatellite(gameItem) && this.showSatellite)
	}

	private isGuaranteed(gameItem: GameObject): boolean {
		return gameItem.category === 'GUARANTEED' && !this.isSatellite(gameItem)
	}

	private isRegular(gameItem: GameObject): boolean {
		return !this.isGuaranteed(gameItem) && !this.isSatellite(gameItem) && !this.hasBounty(gameItem) && !this.hasMysteryBounty(gameItem)
	}

	private isStageTournament(gameItem: GameObject): boolean {
		// A stage tournament is actually a satellite tournament that can also be entered via direct buy-in.
		// PRM have requested that these tournament types appear when the user searches for Regular tournaments
		// given we don't have the appropriate data in the lobby ws payload, PRM have agreed to use the "Stage 1 ["
		// tournament naming convention for flight tournaments
		// @ts-ignore -- gameItem contains a name attribute - compile time type checking fail :(
		return this.isSatellite(gameItem) && gameItem.name && gameItem.name.includes('Stage 1 [')
	}

	private isSatellite(gameItem: GameObject): boolean {
		// item.isSatelliteTournament is unreliable, so we check the ticket info instead.
		return gameItem.satelliteTicket != undefined
	}

	private isRebuy(gameItem: GameObject): boolean {
		return gameItem.isRebuyEnabled && !this.isSatellite(gameItem)
	}

	private hasBounty(gameItem: GameObject): boolean {
		return gameItem.bountyFee > 0
	}

	private hasMysteryBounty(gameItem: GameObject): boolean {
		return gameItem.mysteryBountyFee > 0 || gameItem.mysteryBountyGuaranteedPrizePool > 0
	}
}
